import { useMemo } from 'react'
import { useGameVersion } from 'app/frontend/base/hooks/useGameVersion'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'
import { EventActivityParticipantEntry } from '../components/eventActivityParticipantEntry'
import { resolveEventParticipantCharacter } from '../components/eventActivityParticipants'
import { resolveSelectionOverrideDisplay } from '../components/eventSelectionOverrideDisplay'
import {
  EventPlanOverridesByPlanId,
  getEncounterAssignmentAbilityHidden,
  getPlanEncounterAssignmentOverrides,
} from '../components/eventActivityPlanOverrides'
import { ActivityAssignmentViewModel } from '../components/activityAssignmentViewModel'
import {
  EventActivityLinkedPlanData,
  EventActivityResolvedAssignmentAbility,
} from './useEventActivityLinkedPlanData'

export const useActivityAssignmentViewModels = (
  entry: EventActivityParticipantEntry,
  activeActivities: wowaudit.EventActivity[],
  linkedPlanDataByActivityId: Map<number, EventActivityLinkedPlanData>,
) => {
  const gameVersion = useGameVersion()
  const hasRaidPlans = gameVersion.has('raid_plans')
  const isUnavailableParticipant = unavailableParticipantStatuses.includes(
    entry.participant.status,
  )
  const activityViewModels = useMemo(() => {
    const viewModels: ActivityAssignmentViewModel[] = []
    activeActivities.forEach((activity) => {
      const selection = entry.selections[activity.id]
      if (!selection) return
      const displayCharacter = resolveEventParticipantCharacter(
        entry,
        activity.id,
      )
      const linkedPlanData = linkedPlanDataByActivityId.get(activity.id)
      const actorSpellIds = (linkedPlanData?.raidPlans ?? [])
        .flatMap((raidPlanData) => raidPlanData.resolvedActors)
        .filter(
          (resolvedActor) =>
            resolvedActor.participant?.characterId === displayCharacter.id,
        )
        .flatMap((resolvedActor) => resolvedActor.actor.casts)
        .filter((cast) => !cast.hidden)
        .map((cast) => cast.spell_id)
      const generalSpellIds = (linkedPlanData?.raidPlans ?? [])
        .flatMap(
          (raidPlanData) =>
            raidPlanData.linkedRaidPlan.timeline.general?.casts ?? [],
        )
        .filter((cast) => !cast.hidden)
        .map((cast) => cast.spell_id)
      const cooldownRowCount = new Set(
        [...actorSpellIds, ...generalSpellIds].map((spellId) =>
          spellId === null ? 'spell:null' : `spell:${spellId}`,
        ),
      ).size
      const isSelected = !isUnavailableParticipant && selection.selected
      const selectionOverrideDisplay = resolveSelectionOverrideDisplay({
        classId: displayCharacter.classId,
        role: selection.role,
        specId: selection.specId,
        mainRole: entry.role,
        mainSpecId: entry.specId,
      })
      const hasCharacterOverride = selection.characterId !== entry.characterId
      const assignmentAbilitiesByKey = new Map<
        string,
        EventActivityResolvedAssignmentAbility
      >()
      ;(linkedPlanData?.raidPlans ?? []).forEach((raidPlanData) => {
        const encounterAssignmentOverrides =
          getPlanEncounterAssignmentOverrides(
            activity.planOverrides as EventPlanOverridesByPlanId,
            raidPlanData.linkedRaidPlan.id,
          )
        ;(
          raidPlanData.resolvedAssignmentAbilitiesByCharacterId[
            displayCharacter.id
          ] ?? []
        ).forEach((assignmentAbility) => {
          if (
            getEncounterAssignmentAbilityHidden({
              encounterAssignments: encounterAssignmentOverrides,
              abilityKey: assignmentAbility.abilityKey,
            })
          ) {
            return
          }
          assignmentAbilitiesByKey.set(
            assignmentAbility.abilityKey,
            assignmentAbility,
          )
        })
      })
      const assignmentAbilities = Array.from(assignmentAbilitiesByKey.values())
      viewModels.push({
        activity,
        linkedPlanData,
        displayCharacter,
        selection,
        isSelected,
        hasCharacterOverride,
        selectionOverrideDisplay,
        totalAbilityCount: assignmentAbilities.length + cooldownRowCount,
        isOpenable: hasRaidPlans && activity.activityType === 'raids',
      })
    })
    return viewModels
  }, [
    activeActivities,
    entry,
    hasRaidPlans,
    isUnavailableParticipant,
    linkedPlanDataByActivityId,
  ])
  const openableEncounters = useMemo(
    () => activityViewModels.filter((viewModel) => viewModel.isOpenable),
    [activityViewModels],
  )
  return { activityViewModels, openableEncounters }
}
